








































































































































































  import { computed, defineComponent, PropType, ref, watch, reactive } from 'vue'
  import { Cart, Shared } from '@one/types/dist/orderpath/app'
  import { AxiosResponse } from 'axios'
  import { isNotNull } from '@one/core'
  import { StateChanger } from 'vue-infinite-loading'

  import { StockVisibilityMode } from '@one/types/dist/orderpath/app/shared'
  import useCurrentInstance from '~/utils/useCurrentInstance'
  import useUtils from '~/utils/useUtils'
  import { priceFormatter } from '~/utils/filters'

  export interface AwardProductChanged {
    id: string
    quantity: number
    shouldBeRemoved?: boolean
  }
  export interface AwardsUpdateEvent {
    awardsDeleted: Array<AwardProductChanged>
    awardsAdded: Array<AwardProductChanged>
    isMultiple?: boolean
  }
  export interface InitialAwards {
    id: string,
    selected: number,
    quantity: number
  }

  export default defineComponent({
    name: 'OneCampaignModal',
    components: {
      OneIconLoader: () => import('~/components/molecules/icons/OneIconLoader.vue'),
      InfiniteLoading: () => import('vue-infinite-loading'),
    },
    props: {
      value: {
        type: Boolean,
        required: true,
      },
      progress: {
        type: Boolean,
        required: false,
        default: false,
      },
      campaignId: {
        type: String as PropType<string | null>,
        required: false,
        default: null,
      },
      initiallySelectedAwardsAmount: {
        type: Number,
        required: false,
        default: 0,
      },
    },
    setup(props, { emit }) {
      const vm = useCurrentInstance()
      const { priceTypeLabel } = useUtils(vm)

      const isLoadError = ref<boolean>(false)
      const error = ref<any>(null)
      const selectedAwards = ref<Array<string>>([])
      const promotion = ref<Cart.Responses.CampaignAwardsRestView | null>(null)
      const pageNumber = ref<number>(1)
      const pageSize = ref<number>(18)
      const availableAwards = ref<Array<Shared.CampaignAwardRestView>>([])
      const initialAwards = ref<Array<InitialAwards>>([])
      const hasNextPage = ref<boolean>(true)
      const productSearch = ref<string>('')
      const selectedAwardsAmount = ref<number>(0)
      const awardsAdded = ref<Array<string>>([])
      const awardsDeleted = ref<Array<string>>([])
      const multipleChoice = ref<boolean>(false)
      const tooMuchQuantityForProduct = reactive({
        disabled: false,
        id: '',
      })
      const selectedWarehouse = reactive({
        local: null,
        parent: null,
      })

      const getCurrentCartId = computed(() => vm.$store.getters['cart/getCurrentCartId'])
      const getCurrency = computed(() => vm.$store.getters['layout/getCurrency'])
      const isGrossSelected = computed(() => vm.$store.getters['layout/isGrossSelected'])
      const getDefaultWarehouseId = computed(() => vm.$store.getters['stocks/getDefaultWarehouseId'])
      const cart = computed(() => vm.$store.getters['cart/getCurrentCart'])
      const stockVisibility = computed(() => cart.value?.stockVisibility === StockVisibilityMode.HIDDEN)

      const isAwardsCountLimitReached = computed(() => {
        return promotion.value ? promotion.value.available <= selectedAwards.value.length : false
      })

      const selectedMultipleAwardsAmount = computed(() => {
        return Number(
          availableAwards.value
            .map(item => item.selected)
            .reduce((prev, next) => Number(prev) + Number(next), 0),
        )
      })

      const isTooMuchAwardsSelected = computed(() => {
        return multipleChoice.value && promotion.value
          ? selectedMultipleAwardsAmount.value > promotion.value.available
          : false
      })

      const selectedPromotionsCounter = computed(() => {
        return multipleChoice.value
          ? `${selectedMultipleAwardsAmount.value}/${promotion.value?.available}`
          : `${selectedAwardsAmount.value}/${promotion.value?.available}`
      })

      const price = computed(() => {
        return (product: Shared.CampaignAwardRestView) => {
          return isGrossSelected.value ? product.prices.contentUnitGross : product.prices.contentUnitNet
        }
      })

      const updatedAwards = computed<AwardsUpdateEvent>(() => {
        return {
          awardsDeleted: awardsDeleted.value.map((id: string) => {
            return awardsAdded.value.includes(id)
              ? null
              : {
                id: id,
                quantity: -(getProductQuantity(id)),
              }
          }).filter(isNotNull) as Array<AwardProductChanged>,
          awardsAdded: awardsAdded.value.map((id: string) => {
            return awardsDeleted.value.includes(id)
              ? null
              : {
                id: id,
                quantity: getProductQuantity(id),
              }
          }).filter(isNotNull) as Array<AwardProductChanged>,
        }
      })

      const updatedMultipleAwards = computed(() => {
        const newSelectedAwards = availableAwards.value.filter((award) => {
          if (award.selected) {
            return initialAwards.value.find((item) => {
              return award.id === item.id && award.selected > item.selected
            })
          }
          return false
        })

        const productsToAdd = newSelectedAwards.map((award) => {
          return {
            id: award.id,
            quantity: calculateProductsQuantity(award),
          }
        })

        const unselectedAwards = availableAwards.value.filter((award) => {
          return initialAwards.value.find((item) => {
            return award.id === item.id && award.selected < item.selected
          })
        })

        const productsToRemove = unselectedAwards.map((award) => {
          return {
            id: award.id,
            quantity: Math.abs(calculateProductsQuantity(award)),
            shouldBeRemoved: !award.selected,
          }
        })

        return {
          isMultiple: true,
          awardsAdded: productsToAdd,
          awardsDeleted: productsToRemove,
        }
      })

      const getProductQuantity = (id: string) => {
        return availableAwards.value.find(award => award.id === id)!.quantity
      }

      const calculateProductsQuantity = (award: Shared.CampaignAwardRestView) => {
        const initialItem = initialAwards.value.find(item => item.id === award.id)
        return (Number(award.selected) - Number(initialItem?.selected || 0)) * award.quantity
      }

      const onCheckboxChange = (value: string) => {
        const indexKey = selectedAwards.value.indexOf(value)
        if (indexKey > -1) {
          awardsDeleted.value.push(value)

          const addedIndex = awardsAdded.value.indexOf(value)
          if (addedIndex > -1) { awardsAdded.value.splice(addedIndex, 1) }

          selectedAwardsAmount.value--
          selectedAwards.value.splice(indexKey, 1)
        } else if (!isAwardsCountLimitReached.value) {
          awardsAdded.value.push(value)

          const deletedIndex = awardsDeleted.value.indexOf(value)
          if (deletedIndex > -1) { awardsDeleted.value.splice(deletedIndex, 1) }

          selectedAwardsAmount.value++
          selectedAwards.value.push(value)
        }
      }

      const onQuantityChange = (award: Shared.CampaignAwardRestView, value: number) => {
        tooMuchQuantityForProduct.disabled = false
        tooMuchQuantityForProduct.id = award.id
        const stock = stockAward(award.id)
        if (value > stock) {
          tooMuchQuantityForProduct.disabled = true
          return
        }
        if (!initialAwards.value.some(item => item.id === award.id)) {
          initialAwards.value = availableAwards.value.map((item) => {
            return {
              id: item.id,
              selected: Number(item.selected),
              quantity: item.quantity,
            }
          })
        }
        award.selected = value
      }

      const onSave = () => {
        if (isTooMuchAwardsSelected.value) { return }
        emit('saved', multipleChoice.value ? updatedMultipleAwards.value : updatedAwards.value)
      }

      const isAwardSelected = (id: string): boolean => {
        return selectedAwards.value.includes(id)
      }

      const onLoadError = (err: unknown) => {
        isLoadError.value = true
        error.value = err
      }

      const isDisabled = (isAwardSel: boolean): boolean => {
        return !isAwardSel && isAwardsCountLimitReached.value
      }

      const fetchAwards = (infiniteScrollState: StateChanger | null) => {
        if (props.campaignId && hasNextPage.value) {
          if (!infiniteScrollState) { vm.$wait.start('fetch-awards') }
          vm.$api.orderpath.app.getPaginatedAwardsForCart(
            getCurrentCartId.value,
            props.campaignId,
            getDefaultWarehouseId.value,
            pageNumber.value,
            pageSize.value,
            productSearch.value,
          )
            .then((res: AxiosResponse<Cart.Responses.CampaignAwardsRestView>) => {
              multipleChoice.value = res.data.multipleChoice
              promotion.value = res.data
              availableAwards.value.push(...promotion.value.awards.items)

              if (!promotion.value.awards.nextPage) {
                hasNextPage.value = false
                if (infiniteScrollState) { infiniteScrollState.complete() }
              } else {
                pageNumber.value++
                if (infiniteScrollState) { infiniteScrollState.loaded() }
              }

              const idsToAdd = promotion.value.awards.items
                .map((award: Shared.CampaignAwardRestView) => {
                  return (award.selected && award.id) || null
                })
                .filter(isNotNull)

              selectedAwards.value.push(...idsToAdd)
            })
            .catch(onLoadError)
            .finally(() => vm.$wait.end('fetch-awards'))
        }
      }

      const getAwardPhoto = (award: Shared.CampaignAwardRestView): string => {
        return award?.photo || 'https://static-demo-images.s3-eu-west-1.amazonaws.com/photo_plcs_min.svg'
      }

      const stockAward = (id: string) => {
        const stock = vm.$store.getters['stocks/getProductStocks'](id)
        return stock?.selectedWarehousesQuantity ?? stock?.available
      }

      const getStock = (productId: string, warehouseId: string) => {
        return vm.$store.getters['stocks/getProductOrderQuantityInWarehouse'](productId, warehouseId)
      }

      watch(productSearch, (newVal, oldVal) => {
        if (typeof oldVal === 'undefined') { return }
        availableAwards.value = []
        hasNextPage.value = true
        pageNumber.value = 1
        fetchAwards(null)
      }, { immediate: true })

      watch(() => props.value, (isVisible: boolean) => {
        if (isVisible) {
          selectedAwardsAmount.value = props.initiallySelectedAwardsAmount
        } else {
          availableAwards.value = []
          selectedAwards.value = []
          initialAwards.value = []
          hasNextPage.value = true
          pageNumber.value = 1
          awardsAdded.value = []
          awardsDeleted.value = []
          selectedAwardsAmount.value = 0
        }
      }, { immediate: true })

      watch(availableAwards, (newAwards) => {
        if (newAwards.length > 0) {
          const skus = newAwards.map(item => item.id)
          const warehouse = vm.$store.getters['stocks/getWarehouse'](cart.value.contextWarehouseId)
          selectedWarehouse.local = warehouse?.parent ? warehouse.id : null
          selectedWarehouse.parent = warehouse.parent ? warehouse.parent : warehouse.id
          const warehousePayload = selectedWarehouse?.parent
            ? [warehouse.id, warehouse.parent]
            : [warehouse.id]
          vm.$store.dispatch('stocks/fetchStocksForProductsAndWarehouses', {
            skus,
            warehouses: warehousePayload,
          })
        }
      }, { deep: true })

      return {
        isLoadError,
        error,
        promotion,
        availableAwards,
        productSearch,
        multipleChoice,
        getCurrency,
        priceTypeLabel,
        isTooMuchAwardsSelected,
        selectedPromotionsCounter,
        price,
        onCheckboxChange,
        onQuantityChange,
        onSave,
        isAwardSelected,
        isDisabled,
        fetchAwards,
        getAwardPhoto,
        priceFormatter,
        cart,
        selectedWarehouse,
        stockAward,
        stockVisibility,
        getStock,
        tooMuchQuantityForProduct,
      }
    },
  })
